import clsx from "clsx";
import type dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import { DateRangePicker } from "../DateRangePicker";
import { errorTextID, labelID } from "../utils";
import { FieldsError } from "./Error";

interface FieldsDateRangePickerProps {
  id: string;
  startDay?: dayjs.Dayjs;
  endDay?: dayjs.Dayjs;
  labelText?: string;
  errorText?: string[];

  startLabelText?: string;
  startErrorText?: string[];
  endLabelText?: string;
  endErrorText?: string[];

  className?: string;
  disabled?: boolean;
  name?: (text: string) => string;
  format?: (day: dayjs.Dayjs) => string;
}

export function FieldsDateRangePicker({
  startDay: startDayProp,
  endDay: endDayProp,
  labelText,
  errorText,
  startLabelText,
  startErrorText,
  endLabelText,
  endErrorText,
  id,
  className,
  disabled,
  format,
  ...props
}: FieldsDateRangePickerProps) {
  const [hasJS, setHasJS] = useState(false);

  const startDayInputID = `${id}:StartDay`;
  const startDayInputName = props.name ? props.name("start_day") : "start_day";
  const [startDay, setStartDay] = useState(startDayProp);
  const [startDayValue, setStartDayValue] = useState("");
  const handleStartDayChange = useCallback(
    (day: dayjs.Dayjs) => {
      setStartDayValue(format ? format(day) : day.toISOString());
      setStartDay(day);
    },
    [format]
  );

  const endDayInputID = `${id}:EndDay`;
  const endDayInputName = props.name ? props.name("end_day") : "end_day";
  const [endDay, setEndDay] = useState(endDayProp);
  const [endDayValue, setEndDayValue] = useState("");
  const handleEndDayChange = useCallback(
    (day: dayjs.Dayjs) => {
      setEndDayValue(format ? format(day) : day.toISOString());
      setEndDay(day);
    },
    [format]
  );

  useEffect(() => {
    setHasJS(true);
  }, []);

  return (
    <div>
      <fieldset
        className={clsx("flex", "flex-col", "space-y-2", className)}
        disabled={disabled}
        aria-labelledby={labelID(id)}
        aria-describedby={errorTextID(id)}
      >
        {errorText && <FieldsError id={errorTextID(id)}>{errorText}</FieldsError>}
        <div className="relative">
          <legend
            id={labelID(id)}
            className={clsx(
              "relative",
              "block",
              "font-sans",
              "font-bold",
              "text-base",
              "text-gray-700",
              "leading-none",
              "tracking-tight",
              "mb-2"
            )}
          >
            {labelText}
          </legend>
          <label htmlFor={startDayInputID} className="visually-hidden">
            <span id={labelID(startDayInputID)}>{startLabelText}</span>
            {startErrorText && <FieldsError id={errorTextID(startDayInputID)}>{startErrorText}</FieldsError>}
            <input
              id={startDayInputID}
              type="text"
              value={startDayValue}
              onChange={(e) => setStartDayValue(e.target.value)}
              name={startDayInputName}
              aria-labelledby={labelID(startDayInputID)}
              aria-describedby={errorTextID(startDayInputID)}
              aria-invalid={startErrorText ? "true" : undefined}
              tabIndex={hasJS ? -1 : 0}
            />
          </label>
          <label id={labelID(endDayInputID)} htmlFor={endDayInputID} className="visually-hidden">
            <span id={labelID(endDayInputID)}>{endLabelText}</span>
            {endErrorText && <FieldsError id={errorTextID(endDayInputID)}>{endErrorText}</FieldsError>}
            <input
              id={endDayInputID}
              type="text"
              aria-hidden={true}
              value={endDayValue}
              onChange={(e) => setEndDayValue(e.target.value)}
              name={endDayInputName}
              aria-labelledby={labelID(endDayInputID)}
              aria-describedby={errorTextID(endDayInputID)}
              aria-invalid={endErrorText ? "true" : undefined}
              tabIndex={hasJS ? -1 : 0}
            />
          </label>
          <DateRangePicker
            firstDayOfWeek={1}
            startDay={startDay}
            onStartDayChange={handleStartDayChange}
            endDay={endDay}
            onEndDayChange={handleEndDayChange}
          />
        </div>
      </fieldset>
    </div>
  );
}
